import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import {useCallback, useId, useState} from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {CompactCell} from "@khanacademy/wonder-blocks-cell";
import {View, type StyleType} from "@khanacademy/wonder-blocks-core";
import {Checkbox} from "@khanacademy/wonder-blocks-form";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {ModalDialog, ModalPanel} from "@khanacademy/wonder-blocks-modal";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {HeadingSmall, LabelMedium} from "@khanacademy/wonder-blocks-typography";
import {publishAnalyticsEventForMinorConversion} from "@khan/analytics-learning-time";
import {
    updateConversationWithSystemMessage,
    useGuideAudioController,
    useGuidePreferences,
} from "@khan/guide-core";
import {GuideStateApi} from "@khan/guide-state";
import {guideVoices} from "@khan/guide-utils";
import {doNotTranslate} from "@khan/i18n";
import * as MobileApp from "@khan/mobile-app-integration";
import {mediaQueries} from "@khan/shared-styles";

import {SoundWaves} from "../sound-waves.tsx";

import checkIcon from "@phosphor-icons/core/bold/check-bold.svg";
import speakerIcon from "@phosphor-icons/core/regular/speaker-simple-high.svg";

interface Props {
    /**
     * Optional styles to be applied to the modal.
     */
    style?: StyleType;

    /**
     * Function to close the modal.
     */
    onClose: () => void;
}

/**
 * Renders a voice selection modal.
 *
 * This is a modal that allows the user to select a voice for the Guide
 * text-to-speech feature. It is used as part of text-to-speech onboarding.
 */
export const TextToSpeechVoiceSelectionModal = (props: Props) => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [enableVoiceOnComplete, setEnableVoiceOnComplete] = useState(false);
    const {
        audioKey,
        isPlaying,
        play: playAudio,
        setSelectedVoice,
        stop: stopAudio,
        setIsVoiceOn,
    } = useGuideAudioController();
    const {style, onClose} = props;

    // The ID used by Screen Readers to identify the dialog title.
    const dialogTitleId = useId();

    const {preferences, setPreferences} = useGuidePreferences();
    const voiceOptions = guideVoices.map((voiceDescriptor, index) => {
        const voiceSampleId = `${voiceDescriptor}:${preferences.guideLanguageCode}`;
        const isActive = activeIndex === index;
        const isPlayingVoiceOption = audioKey === voiceSampleId && isPlaying;

        const rightAccessory = isPlayingVoiceOption ? (
            <SoundWaves />
        ) : isActive ? (
            <PhosphorIcon icon={speakerIcon} size="small" />
        ) : null;

        const leftAccessory = (
            <PhosphorIcon
                color={color.blue}
                icon={checkIcon}
                size="small"
                style={!isActive && styles.hidden}
            />
        );

        return (
            <View key={voiceDescriptor} role="listitem">
                <CompactCell
                    title={doNotTranslate`Khanmigo ${index + 1}`}
                    rightAccessory={rightAccessory}
                    leftAccessory={leftAccessory}
                    horizontalRule="inset"
                    onClick={() => {
                        setActiveIndex(index);
                        if (voiceDescriptor) {
                            playAudio(voiceSampleId);
                        }
                    }}
                />
            </View>
        );
    });

    const handleSelectVoice = useCallback(() => {
        const voice = guideVoices[activeIndex];
        // In practice, it isn't possible to get a NULL value for descriptor
        // but we type-refine here for TypeScript.
        if (!voice) {
            return;
        }

        setSelectedVoice(voice);

        if (enableVoiceOnComplete) {
            publishAnalyticsEventForMinorConversion({
                eventType: "AiGuideTextToSpeechToggled",
                eventSchemaVersion: 1,
                aiGuideThreadId: GuideStateApi.latest().threadId ?? "",
                aiGuideNavigationId: GuideStateApi.latest().navigation.id,
                isTextToSpeechOn: true,
            });
            setIsVoiceOn(true);
            GuideStateApi.set(
                "conversation",
                updateConversationWithSystemMessage(
                    GuideStateApi.latest().conversation,
                    t`Khanmigo voice enabled`,
                ),
            );
        }
        setPreferences({guideVoiceDescriptor: voice});
        stopAudio();
        publishAnalyticsEventForMinorConversion({
            eventType: "AiGuideVoiceSelected",
            eventSchemaVersion: 1,
            aiGuideSelectedVoice: voice,
        });
        onClose();
    }, [
        activeIndex,
        enableVoiceOnComplete,
        onClose,
        setPreferences,
        setIsVoiceOn,
        setSelectedVoice,
        stopAudio,
    ]);

    MobileApp.Guide.useUXStack(
        "guide-text-to-speech-onboarding-voice-selection",
        {
            onCancel: onClose,
            canConfirm: true,
            onConfirm: handleSelectVoice,
            translatedTitle: t`Select a voice`,
            translatedCancelLabel: t`Close`,
            translatedConfirmLabel: t`Done`,
        },
    );

    return (
        <ModalDialog style={style} aria-labelledby={dialogTitleId}>
            <ModalPanel
                closeButtonVisible
                content={
                    <View>
                        <View style={styles.body}>
                            <HeadingSmall id={dialogTitleId}>
                                {t`Select a voice`}
                            </HeadingSmall>
                            <Strut size={spacing.xSmall_8} />
                            <LabelMedium>
                                {t`Pick a voice for Khanmigo. You can always change it in the Settings menu.`}
                            </LabelMedium>
                        </View>
                        <Strut size={spacing.large_24} />
                        <View
                            role="list"
                            style={[styles.body, styles.voiceList]}
                        >
                            {voiceOptions}
                        </View>
                        <Strut size={spacing.large_24} />
                        <Checkbox
                            checked={enableVoiceOnComplete}
                            label={t`Enable voice now`}
                            style={styles.enableVoice}
                            onChange={setEnableVoiceOnComplete}
                        />
                    </View>
                }
                footer={
                    <View style={styles.footer}>
                        <Button kind="secondary" onClick={handleSelectVoice}>
                            {t`Done`}
                        </Button>
                    </View>
                }
            />
        </ModalDialog>
    );
};

const styles = StyleSheet.create({
    body: {
        width: "unset",
        marginInlineStart: -spacing.xSmall_8,
        marginInlineEnd: -spacing.xSmall_8,
        [mediaQueries.smOrSmaller]: {
            marginInlineStart: spacing.xSmall_8,
            marginInlineEnd: spacing.xSmall_8,
        },
    },
    enableVoice: {
        marginBlockEnd: -spacing.medium_16,
        [mediaQueries.smOrSmaller]: {
            marginInlineStart: spacing.xSmall_8,
            marginInlineEnd: spacing.xSmall_8,
        },
    },
    voiceList: {
        borderRadius: 8,
        overflow: "hidden",
    },
    footer: {
        paddingBlockEnd: spacing.medium_16,
        paddingInlineStart: spacing.xSmall_8,
        paddingInlineEnd: spacing.xSmall_8,
    },
    hidden: {
        visibility: "hidden",
    },
});
